"use client";

import { useQuery } from "@tanstack/react-query";

import { industryQuery } from "@/api/industry";
// import { resourceQuery } from "@/api/resource";
import { serviceQuery } from "@/api/service";
// import { blogQuery } from "@/api/blog";

import { IHeaderMenuItem } from "@/components/layouts/header/DesktopHeader";
import {
  ICategory,
  ISubCategory,
} from "@/components/layouts/header/MegaMenuDropdown";
import { IServiceResponse } from "@/api/dtos/service.type";

import { trimEnd, trimStart } from "lodash";
import { navigationQuery } from "@/api/navigation";
import { getImageUrl } from "@/utils/media.helper";
import { IMenuBookDownloadSection } from "@/api/dtos/block-component.type";

import { env } from "@/env.mjs";

// -------------------------------------------------------------------------------------------

// if current path is one of the companies or resources or dynamic routes then show bottom border
const options = {
  borderActive: (m: IHeaderMenuItem, path: string): string => {
    const currentPathSegment = path.split("/")[1];

    const baseUrl = trimEnd(env.NEXT_PUBLIC_SITE_URL, "/");

    return m.items?.some((el) => {
      if (!el.href) return false;

      try {
        const hrefPath = new URL(el.href, baseUrl).pathname;
        const hrefSegment = hrefPath.split("/")[1];
        return hrefSegment === currentPathSegment;
      } catch (error) {
        // If URL construction fails, fallback to simple path comparison
        const hrefPath = el.href.startsWith("/") ? el.href : `/${el.href}`;
        const hrefSegment = hrefPath.split("/")[1];
        return hrefSegment === currentPathSegment;
      }
    })
      ? "!border-blue-800"
      : "";
  },
};

const useNavigation = () => {
  const { data: services } = useQuery({
    ...serviceQuery.all(),
    select: (data) => {
      return getServicesNavigationItem("Services", "/services", data, {
        width: "full",
        // if current path is one of the services routes then show bottom border to services menu
        borderActive: (m, path) => {
          return path.split("/")[1] === "services" ? "!border-blue-800" : "";
        },
      });
    },
  });

  // Dynamic Industry navigation menu
  const { data: industries } = useQuery({
    ...industryQuery.all(),
    select: (data) => {
      return getIndustriesNavigationItem("Industry", "/industries", data.data, {
        // if current path is one of the industries routes then show bottom border to industries menu
        borderActive: (m, path) => {
          return path.split("/")[1] === "industries" ? "!border-blue-800" : "";
        },
      });
    },
  });

  const { data: industryDownloadBook } = useQuery({
    ...industryQuery.getIndustry(),
    select: (data) => {
      return getDownloadBookData(data.data.attributes.download_book);
    },
  });

  const { data: navigation } = useQuery({
    ...navigationQuery.getNavigation(),
    select(data) {
      return data.data.attributes.navigation.map(
        ({ nav_category, nav_links, download_book }) => ({
          options,
          label: nav_category,
          items: nav_links.map(({ name, link, header, footer }) => ({
            label: name,
            href: link,
            header,
            footer,
          })),
          downloadBook: getDownloadBookData(download_book),
        }),
      );
    },
  });

  const industryData = {
    label: industries?.label ?? "",
    href: industries?.href ?? "",
    items: industries?.items ?? [],
    options: industries?.options,
    downloadBook: industryDownloadBook,
  };

  return {
    services,
    industries: industryData,
    navigation: navigation ?? [],
  };
};

export default useNavigation;

// getNavigationItem function will generate all the items(navbar links) which needs to add in the navbar
const getIndustriesNavigationItem = <T>(
  label: string,
  href: string,
  items: T,
  options?: {
    width?: "full" | "fit";
    borderActive?: (m: IHeaderMenuItem, path: string) => string;
  },
) => {
  const itemsValue = (items as any[]).map((item) => ({
    label: item?.attributes?.page_name || item?.attributes?.title,
    href: `${href}/${item.attributes.slug}`,
    rank: item?.attributes?.rank,
  }));

  itemsValue.sort((a, b) => (a.rank && b.rank ? a.rank - b.rank : -1));

  return {
    label,
    href,
    items: itemsValue,
    options,
  };
};

const getServicesNavigationItem = (
  label: string,
  href: string,
  items: IServiceResponse,
  options?: {
    width?: "full" | "fit";
    borderActive?: (m: IHeaderMenuItem, path: string) => string;
  },
) => {
  const findOrCreateCategory = (
    categories: ICategory[],
    categoryHeading: string,
    categoryRank: number,
    categoryLink: string | null,
    downloadBook: IMenuBookDownloadSection | null | undefined,
  ) => {
    if (!categoryHeading) return null;

    let category = categories.find((c) => c.heading === categoryHeading);
    if (!category) {
      category = {
        heading: categoryHeading,
        rank: categoryRank,
        categoryLink: categoryLink
          ? "/" + trimStart(categoryLink, "/")
          : undefined,
        subCategory: [],
        downloadBook: getDownloadBookData(downloadBook),
      };
      categories.push(category);
    } else if (!category.categoryLink && categoryLink) {
      category.categoryLink = categoryLink;
    }
    return category;
  };

  const findOrCreateSubCategory = (
    subCategories: ISubCategory[] | undefined,
    subCategoryHeading: string,
    subCategoryRank: number,
  ) => {
    if (!subCategories) return null;

    let subCategory = subCategories.find(
      (sc) => sc.heading === subCategoryHeading,
    );
    if (!subCategory) {
      subCategory = {
        heading: subCategoryHeading,
        rank: subCategoryRank,
        items: [],
      };
      subCategories.push(subCategory);
    }
    return subCategory;
  };

  const categories: ICategory[] = [];

  items.data.forEach((item) => {
    const { services_category, services_sub_category, page_name, slug } =
      item.attributes;

    if (!services_category) {
      return;
    }

    const categoryData = services_category.data?.attributes;
    const subCategoryData = services_sub_category?.data?.attributes;

    if (!categoryData) {
      return;
    }

    // Find or create the category
    const categoryObject = findOrCreateCategory(
      categories,
      categoryData.category,
      categoryData.rank,
      categoryData.category_link,
      categoryData.download_book,
    );

    if (!categoryObject) {
      return;
    }

    // If subCategoryData is present, process subcategories
    if (subCategoryData) {
      const subCategoryObject = findOrCreateSubCategory(
        categoryObject.subCategory,
        subCategoryData.sub_category,
        subCategoryData.rank,
      );

      // Add the item to the sub-category
      subCategoryObject?.items.push({
        label: page_name,
        href: `${href}/${slug}`,
      });
    }
  });

  // Sort categories and subcategories by rank
  categories.sort((a, b) => (a.rank && b.rank ? a.rank - b.rank : -1));
  categories.forEach((category) => {
    category?.subCategory?.sort((a, b) =>
      a.rank && b.rank ? a.rank - b.rank : -1,
    );
  });

  // Return the final structure
  return {
    label,
    href,
    items: categories,
    options,
  };
};

/**
 * Handle to formate the menu book download section content
 * @param downloadBook get menu book download section content of strapi
 * @returns the formatted data.
 */
const getDownloadBookData = (
  downloadBook: IMenuBookDownloadSection | null | undefined,
) => {
  return downloadBook &&
    (downloadBook.heading || downloadBook.image || downloadBook.button)
    ? {
        heading: downloadBook.heading,
        image: {
          src: getImageUrl(downloadBook.image),
          alt: downloadBook.image?.name,
        },
        button: {
          ...downloadBook.button,
          text: downloadBook.button?.title,
          link: downloadBook.button?.href,
          id: downloadBook.button?.btn_id,
        },
      }
    : undefined;
};
